import {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo
} from 'react';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { debounce } from '@thd-olt-functional/utils';
import { getInitialMessage } from '../shared/utils/session-utils';
import { checkGenerateQuestionsNeeded } from '../shared/utils/suggestions';
import { announceAssistantReady } from '../platform/events/lifecycle';
import { logger } from '../shared/utils/logger';
// Web Worker-backed store — see .claude/context/message-history.md.
import { useMessageStore } from '../platform/persistence/messageStore';
import { conversationRecord, isNewSessionGeneration } from '../platform/session/session';
import { FLAGS } from '../platform/config/flags';
import { isCustomerTypeAllowed } from '../platform/config/surface';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { reconcileMessages } from '../shared/utils/message-reconcile';
import { normalizeLegacyMessageType } from '../shared/message';

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @param { Object } session
 * @param { { getState: () => object } } [assistantStore] - the assistant store. When passed, this
 *   hook publishes its message-history + ISQ state into it directly (see the publish effects near
 *   the return), so the provider no longer bridges these values via effects. Optional so the hook
 *   stays usable without a store (e.g. unit tests that renderHook it in isolation). Named to avoid
 *   colliding with `store` below, which is the message-persistence store (useMessageStore).
 * @param { Function } [submitGenerateQuestions] - the query layer's ISQ trigger capability.
 * @param { boolean } [enabled] - false while the holder has no query layer: init never runs, so
 *   nothing is pruned, cleared or seeded in assistantBotDB and readiness is not announced.
 *   Flips false → true at most once. See .claude/context/message-history.md.
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (
  withPersistence = true, session, assistantStore, submitGenerateQuestions = () => {}, enabled = true,
) => {
  const {
    isConsumerApp,
    channel,
    customer,
    deliveryZip
  } = useContext(ExperienceContext);

  const isDynamicIsqMasterEnabled = useConfigService(FLAGS.dynamicIsqMasterEnabled);
  const isDynamicIsqDtFsEnabled = useConfigService(FLAGS.dynamicIsqDtEnabled);
  const isDynamicIsqMwFsEnabled = useConfigService(FLAGS.dynamicIsqMwEnabled);
  const isDynamicIsqAppFsEnabled = useConfigService(FLAGS.dynamicIsqAppEnabled);
  const isDynamicIsqB2BEnabled = useConfigService(FLAGS.dynamicIsqB2bEnabled);
  const isDynamicIsqValidCustomerType = isCustomerTypeAllowed(customer?.type, isDynamicIsqB2BEnabled);

  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const hasHistoryInitialized = useRef(false);
  const initializationLock = useRef(false);
  const prevCustomerType = useRef(customer?.type);
  const [messages, setMessages] = useState([getInitialMessage(session.sessionId)]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCallGeneratedQuestions, setCallToGenerateQuestions] = useState(false);

  // Scoped to this holder's session: a tab open across a session rotation must not render
  // a peer tab's conversation (see messageStore#useMessageStore).
  const store = useMessageStore(session.sessionId);

  const loadIDBMessages = () => {
    setIsLoading(true);
    return store.getAll()
      .then((savedMessages) => {
        // Coerce legacy persisted types (uppercase 'AGENT' → 'agent') on read, before
        // sort/reconcile, so rows written before the casing normalization still render.
        const normalized = savedMessages.map(normalizeLegacyMessageType);
        const sortedMessages = [...normalized].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages((prev) => reconcileMessages(prev, sortedMessages));
        setIsLoading(false);
        return normalized;
      });
  };

  const updateMessage = async (message, updates, reload = false) => {
    /* global structuredClone */
    const updatedMessage = { ...structuredClone(message), ...updates, updatedAt: Date.now() };

    await store.update(updatedMessage);
    if (reload) await loadIDBMessages();
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const saveMessage = async (newMessage) => {
    // PII redaction on persisted agent-mode messages is deliberately unbuilt (WS-8.3) —
    // see .claude/context/message-history.md before implementing it.
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };
    if (!isStorageAvailable.current) {
      // Functional updater — closing over `messages` dropped saves landed in the same tick.
      setMessages((curr) => [...curr, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.sessionId) return Promise.reject(new Error('Session ID not available'));

    return store.add(updatedMessage)
      .then(loadIDBMessages)
      .then((savedMessages) => savedMessages.at(-1));
  };

  /**
   * Cross-tab safe variant of saveMessage for events that fan out to every
   * tab (e.g. inbound Sprinklr agent messages). Uses a single IndexedDB
   * `readwrite` transaction to check for an existing record with the same
   * `messageId` and add only if absent. IDB serializes overlapping
   * `readwrite` transactions across browsing contexts, so exactly one tab
   * persists the row.
   *
   * Resolves to the saved message when this call wrote it, or `null` when a
   * peer tab had already written the same `messageId`.
   */
  const saveMessageIfNew = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
      updatedAt: Date.now(),
    };

    if (!isStorageAvailable.current) {
      if (!updatedMessage.messageId) {
        setMessages((curr) => [...curr, updatedMessage]);
        return updatedMessage;
      }
      let didAppend = false;
      setMessages((curr) => {
        if (curr.some((msg) => msg.messageId === updatedMessage.messageId)) return curr;
        didAppend = true;
        return [...curr, updatedMessage];
      });
      return didAppend ? updatedMessage : null;
    }
    if (!session.sessionId) throw new Error('Session ID not available');
    // No messageId (e.g. LIVE_AGENT_CONNECTING_MESSAGE) — nothing to dedup, plain add.
    if (!updatedMessage.messageId) {
      return store.add(updatedMessage)
        .then(loadIDBMessages)
        .then((all) => all.at(-1));
    }

    // Cross-tab dedup add: the cursor scan + conditional insert run atomically
    // inside the worker (see idb-core#insertIfAbsent), matching on `messageId`.
    const wasInserted = await store.insertIfAbsent(updatedMessage, 'messageId');
    await loadIDBMessages();
    if (wasInserted) broadCastMessageUpdate();
    return wasInserted ? updatedMessage : null;
  };

  // Delete every non-initial message. The cursor walk now runs in the worker.
  const clearMessageHistory = () => store
    .clearMessages({ keepInitial: true })
    .then(loadIDBMessages);

  const clearHistory = debounce(50, clearMessageHistory); // 50ms delay for consistency

  // Startup prune (stale rows, duplicate initials, blanked b2b suggestions) — runs
  // in the worker (idb-core#cleanOrphans).
  const cleanIndexDb = () => store.cleanOrphans({
    sessionId: session.sessionId,
    customerType: customer?.type,
  });

  /**
   * Drop a conversation left over from a previous session generation, then re-watermark.
   *
   * The conversation domain PULLS this rather than being told: identity is written by the
   * Shell's mount effect, message history mounts later in a lazy chunk, and any transient
   * signal — an event, a threaded prop — is one a document can miss (closed early, chunk
   * failed) with no way to recover it, because the reset has already refreshed the timestamp
   * that made the session stale. The watermark is persisted, so a clear that was owed is still
   * owed on the next load. See platform/session#isNewSessionGeneration for why the sessionId
   * cannot answer this on its own.
   *
   * `keepInitial: false`, unlike the user's "Clear conversation": a new generation gets a
   * genuinely fresh opening message, stamped with the current session and re-offered suggested
   * questions. That is what the two reset reasons that DO change the sessionId already do via
   * cleanOrphans, so this keeps all three behaving alike.
   */
  const clearHistoryFromPreviousSession = async () => {
    const { lastSessionStart } = conversationRecord.get();
    const sessionStart = session.timestamp ?? null;

    if (isNewSessionGeneration(lastSessionStart, sessionStart)) {
      await store.clearMessages({ keepInitial: false });
    }
    if (lastSessionStart !== sessionStart) {
      conversationRecord.update({ lastSessionStart: sessionStart });
    }
  };

  const init = async () => {
    // Prevent multiple simultaneous initializations
    if (initializationLock.current) return;
    initializationLock.current = true;
    try {
      isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
      // Before the prune and the initial-message check below, so a cleared history re-seeds
      // its opening message and re-runs the suggested-questions decision.
      await clearHistoryFromPreviousSession();
      await cleanIndexDb();

      let savedMessages = await store.getAll();
      const initialMessage = savedMessages.find((msg) => msg.isInitialMessage);

      if (!initialMessage) {
        await store.add({ ...getInitialMessage(session.sessionId), updatedAt: Date.now() });
      }
      // Retrieving get all messages again to ensure we have the initial message
      savedMessages = await store.getAll();
      const shouldCallGenerateQuestions = checkGenerateQuestionsNeeded({
        savedMessages,
        channel,
        isConsumerApp,
        isDynamicIsqMasterEnabled,
        isDynamicIsqDtFsEnabled,
        isDynamicIsqMwFsEnabled,
        isDynamicIsqAppFsEnabled,
        isDynamicIsqValidCustomerType
      });
      if (shouldCallGenerateQuestions) {
        setCallToGenerateQuestions(true);

      }

      hasHistoryInitialized.current = true;
      // Deliberately the ONLY announcement point — see .claude/context/event-bus.md
      // ("Never re-announce off drawer traffic").
      announceAssistantReady();

      await loadIDBMessages();
    } finally {
      initializationLock.current = false;
    }
  };

  useEffect(() => {
    // `legacy`: assistantBotDB and readiness belong to its package. See
    // .claude/context/message-history.md.
    if (!enabled) return undefined;
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = createBroadcastChannel(CHANNELS.newMessage);
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = () => {
            logger.log('Received message update broadcast, reloading messages from IndexedDB', messages);
            loadIDBMessages();
          };
        }
      })
      .catch(() => { });

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
        maBroadcastChannel.current = null;
      }
      // Reset initialization lock on unmount
      initializationLock.current = false;
    });
  }, [enabled]);

  // Deciding to ask vs. issuing the query are split (Seq F.d/G.1) — see
  // .claude/context/message-history.md.
  useEffect(() => {
    if (isCallGeneratedQuestions) {
      submitGenerateQuestions({ isConsumerApp, channel, deliveryZip });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCallGeneratedQuestions]);

  /**
   * Patch the opening message with the generated suggested questions.
   *
   * The query layer calls this when its `generateQuestions` operation resolves — it used to be
   * an effect keyed on that operation's response, which is why this hook had to own the
   * operation. Fetching and committing are different jobs; only the commit is message
   * history's, and only it needs the persistence store.
   *
   * @param {object} response  raw `generateQuestions` data.
   */
  const commitSuggestedQuestions = async (response) => {
    const dbMessages = await store.getAll();
    const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
    if (!initialMessage) return;

    initialMessage.content.relatedQuestions = {
      suggestedQuestions: response?.generateQuestions?.suggestedQuestions || [],
      followUpQuestions: [],
      clarifyingQuestions: []
    };
    initialMessage.updatedAt = Date.now();

    await store.update(initialMessage)
      .then(loadIDBMessages)
      .finally(() => { broadCastMessageUpdate(); });
    setCallToGenerateQuestions(false);
  };

  useEffect(() => {
    const prev = prevCustomerType.current;
    prevCustomerType.current = customer?.type;

    if (prev !== 'b2b' && customer?.type === 'b2b') {
      if (isStorageAvailable.current) {
        store.getAll().then((dbMessages) => {
          const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);
          if (!initialMessage) return;
          if (initialMessage?.content?.relatedQuestions?.suggestedQuestions?.length) {
            initialMessage.content.relatedQuestions.suggestedQuestions = [];
            initialMessage.updatedAt = Date.now();
            store.update(initialMessage).then(loadIDBMessages).then(broadCastMessageUpdate);
          }
        });
      } else {
        setMessages((current) => current.map((msg) => (
          // eslint-disable-next-line max-len
          msg.isInitialMessage ? { ...msg, content: { ...msg.content, relatedQuestions: { suggestedQuestions: [], followUpQuestions: [], clarifyingQuestions: [] } } } : msg
        )));
      }
    }
  }, [customer?.type]);

  const { lastBotMessage, lastUserMessage } = useMemo(() => ({
    lastBotMessage: messages.findLast(({ type }) => type === 'bot'),
    lastUserMessage: messages.findLast(({ type }) => type === 'user'),
  }), [messages]);

  // Publishes state directly into the assistant store (WS-10.1) — see
  // .claude/context/message-history.md.
  const historyInitialized = hasHistoryInitialized.current;
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setMessages(messages);
  }, [messages, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastBotMessage(lastBotMessage);
  }, [lastBotMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setLastUserMessage(lastUserMessage);
  }, [lastUserMessage, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setHistoryLoading(isLoading);
  }, [isLoading, assistantStore]);
  useEffect(() => {
    if (assistantStore) assistantStore.getState().setWereQuestionsGenerated(historyInitialized);
  }, [historyInitialized, assistantStore]);

  return {
    messages,
    lastBotMessage,
    lastUserMessage,
    saveMessage,
    saveMessageIfNew,
    historyLoading: isLoading,
    hasHistoryInitialized: hasHistoryInitialized.current,
    clearHistory,
    updateMessage,
    broadCastMessageUpdate,
    // Preserve the hook's public shape for consumers; now worker-backed.
    iDBGetAll_messages: store.getAll,
    iDBUpdate_message: store.update,
    loadIDBMessages,
    // Driven by the query-layer block when its generateQuestions operation resolves.
    commitSuggestedQuestions,
  };
};
